// Ordering for the annotations list panel.
//
// Sorting by type alone is not a total order: every annotation of the same type
// compares equal, so `Array.prototype.sort` (stable) leaves same-type items in
// whatever order they arrived in. With a single-type list -- the common case,
// e.g. notes only -- "Type A -> Z" and "Type Z -> A" then render identically and
// the sort looks broken. Each comparator below therefore falls through to a
// tie-breaker until it reaches a field that is unique in practice, and the
// chosen direction is applied to the whole chain so Z -> A is the exact mirror
// of A -> Z rather than only reversing the (equal) leading key.

import { getAnnotationTitleDisplay, getAnnotationTypeLabel } from './annotationDescriptionDisplay';

export const SORT_BY_TIME = 'time';
export const SORT_BY_TYPE = 'type';

// Annotations are placed on a timeline, so start_time is the meaningful instant;
// created_at is only a fallback for records that never got one. Unparseable
// values collapse to 0 so they sort together instead of poisoning comparisons
// with NaN (NaN comparisons make sort order implementation-defined).
function timestampOf(annotation) {
  const raw = annotation && (annotation.start_time || annotation.created_at);
  const parsed = raw ? Date.parse(raw) : NaN;
  return Number.isFinite(parsed) ? parsed : 0;
}

// Compare on what the user actually reads in the row (the localized label), not
// the raw enum, so the ordering matches the on-screen text in every language.
function typeLabelOf(t, annotation) {
  if (!annotation) return '';
  return getAnnotationTypeLabel(t, annotation.type) || annotation.type || '';
}

function titleOf(t, annotation) {
  if (!annotation) return '';
  return getAnnotationTitleDisplay(t, annotation) || '';
}

// Always returns the ascending comparison; direction is applied by the caller.
// Operates on decorated entries so the expensive label/title formatting is done
// once per annotation rather than once per comparison.
function compareAscending(a, b, sortBy) {
  if (sortBy === SORT_BY_TYPE) {
    const byType = a.typeLabel.localeCompare(b.typeLabel);
    if (byType !== 0) return byType;
    const byTitle = a.title.localeCompare(b.title);
    if (byTitle !== 0) return byTitle;
    const byTime = a.ts - b.ts;
    if (byTime !== 0) return byTime;
    return a.id.localeCompare(b.id);
  }

  const byTime = a.ts - b.ts;
  if (byTime !== 0) return byTime;
  const byTitle = a.title.localeCompare(b.title);
  if (byTitle !== 0) return byTitle;
  // Terminal tie-break on the unique id. Without it two annotations that match
  // on every visible field compare equal, the stable sort preserves input
  // order, and "Z -> A" is no longer the mirror of "A -> Z" for that pair.
  return a.id.localeCompare(b.id);
}

/**
 * Sort annotations for the list panel.
 *
 * @param {Function} t          i18n translate function (labels are compared as displayed)
 * @param {Array}    annotations
 * @param {string}   sortBy     'time' (default) or 'type'
 * @param {string}   sortOrder  'asc' or 'desc'. For time, 'desc' is newest-first;
 *                              for type, 'asc' is A -> Z. Anything other than
 *                              'asc' is treated as 'desc'.
 * @returns {Array} a new, sorted array (the input is not mutated). Nullish
 *                  entries are dropped -- the list renderer dereferences
 *                  `annotation.type` unguarded.
 */
export function sortAnnotations(t, annotations, sortBy, sortOrder) {
  const list = Array.isArray(annotations) ? annotations : [];
  const direction = sortOrder === 'asc' ? 1 : -1;

  // Decorate-sort-undecorate. `titleOf` is not cheap: for the common annotation
  // that carries no base_metadata.title it falls through to the full
  // description-localization pass (regex sweep + a rebuilt alarm-phrase map, ~66
  // t() calls). Calling it inside the comparator made that O(n log n) -- ~11k
  // lookups for 200 same-type notes -- and AnnotationsList re-runs this on every
  // search keystroke. Formatting once per item makes it O(n).
  const decorated = list
    .filter(Boolean)
    .map((annotation, index) => ({
      ref: annotation,
      typeLabel: typeLabelOf(t, annotation),
      title: titleOf(t, annotation),
      ts: timestampOf(annotation),
      // Falls back to the input position so an annotation with no id still has
      // a stable, unique terminal key rather than colliding on ''.
      id: annotation.id != null ? String(annotation.id) : `#${index}`
    }));

  decorated.sort((a, b) => direction * compareAscending(a, b, sortBy));
  return decorated.map(entry => entry.ref);
}

export default sortAnnotations;
